import { theme } from '@buggy/shared'
import type { ApexOptions } from 'apexcharts'
import Image from 'next/image'
import { FunctionComponent, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { SentimentEnum } from '@/api/workspaces-api'
import negativeImageSrc from '@/assets/images/auto-report/negative.png'
import positiveImageSrc from '@/assets/images/auto-report/positive.png'
import { useThemeConstants } from '@/hooks/use-theme-constants'
import type { OurIntlShape } from '@/i18n/typing'
import PlatformImage from '@/utils/export-pptx/components/platform-image'
import {
  SlideContainer,
  SlideTitle,
  Card,
  CardTitle,
} from '@/utils/export-pptx/components/slides/styles'
import { chartSize, PptxObjectType } from '@/utils/export-pptx/constants'
import useApexChartDataUri from '@/utils/export-pptx/hooks/use-apex-chart-data-uri'
import type { CommentSentimentSlide } from '@/utils/export-pptx/types/slide'

interface SlideProps {
  slide: CommentSentimentSlide
  onSlideReady?: (slide: CommentSentimentSlide) => void
  formatMessage: OurIntlShape['formatMessage']
}

const SlideComponent: FunctionComponent<SlideProps> = ({
  slide,
  onSlideReady,
  formatMessage,
}) => {
  const { chartColors } = useThemeConstants()

  const options: ApexOptions = useMemo(() => {
    const { series, labels } = slide.payload.items.reduce<{
      series: number[]
      labels: string[]
    }>(
      (acc, item) => {
        acc.series.push(item.value)
        acc.labels.push(item.label)

        return acc
      },
      { series: [], labels: [] },
    )

    return {
      chart: {
        type: 'donut',
        width: chartSize.donut.w,
        height: chartSize.donut.h,
      },
      plotOptions: {
        pie: {
          donut: {
            size: '70%',
            labels: {
              show: true,
              name: {
                formatter: () => labels[0],
              },
              value: {
                offsetY: 4,
                show: true,
                color: theme.colors.text.primary,
                fontSize: '24px',
                fontFamily: 'Roboto',
                fontWeight: 700,
              },
              total: {
                show: true,
                color: theme.colors.text.secondary,
                fontSize: '16px',
                fontWeight: 400,
                label: slide.payload.title,
                formatter: () => series[0] + '%',
              },
            },
          },
        },
      },
      dataLabels: {
        enabled: true,
        style: { colors: ['#fff'] },
        dropShadow: { enabled: false },
        formatter: (val) => Number(val).toFixed(0) + '%',
      },
      legend: { show: false },
      series,
      labels,
    }
  }, [slide.payload.items, slide.payload.title])

  const dataUri = useApexChartDataUri(options)

  useEffect(() => {
    if (!dataUri) {
      return
    }

    onSlideReady?.(slide)
  }, [onSlideReady, slide, dataUri])

  return (
    <SlideContainer data-pptx-object={PptxObjectType.Shape}>
      <SlideTitle data-pptx-object={PptxObjectType.Shape} mb={12}>
        <PlatformImage
          height={54}
          platform={slide.payload.platform}
          width={54}
        />
        <span data-pptx-object={PptxObjectType.Text}>
          {formatMessage({ id: 'autoreport:analysis_section_comment' })}
        </span>
      </SlideTitle>

      <Card data-pptx-object={PptxObjectType.Shape}>
        <CardTitle data-pptx-object={PptxObjectType.Text} mb={5}>
          {slide.payload.title}
        </CardTitle>

        <CardContent>
          <ChartContainer>
            {dataUri && (
              <Image
                data-original-size
                alt='comment-sentiment-pie-chart'
                data-pptx-object={PptxObjectType.Image}
                height={chartSize.donut.h}
                src={dataUri}
                width={chartSize.donut.w}
              />
            )}
          </ChartContainer>

          <ContentGrid>
            {slide.payload.items.map((item, index) => (
              <ContentItem
                data-pptx-object={PptxObjectType.Shape}
                key={item.label}
              >
                <ContentItemLabel>
                  <div>
                    <ContentItemColor
                      color={chartColors[index]}
                      data-pptx-object={PptxObjectType.Shape}
                    />

                    <span data-pptx-object={PptxObjectType.Text}>
                      {item.label}
                    </span>
                  </div>

                  {item.sentiment === SentimentEnum.Positive && (
                    <Image
                      alt={item.sentiment}
                      data-pptx-object={PptxObjectType.Image}
                      height={40}
                      src={positiveImageSrc}
                      width={40}
                    />
                  )}

                  {item.sentiment === SentimentEnum.Negative && (
                    <Image
                      alt={item.sentiment}
                      data-pptx-object={PptxObjectType.Image}
                      height={40}
                      src={negativeImageSrc}
                      width={40}
                    />
                  )}
                </ContentItemLabel>

                <ContentItemValue data-pptx-object={PptxObjectType.Text}>
                  {item.value}%
                </ContentItemValue>
              </ContentItem>
            ))}
          </ContentGrid>
        </CardContent>
      </Card>
    </SlideContainer>
  )
}

const CardContent = styled.div`
  display: flex;
  gap: 24px;
`

const ChartContainer = styled.div`
  height: ${chartSize.donut.h}px;
`

const ContentGrid = styled.div`
  flex-grow: 1;

  display: grid;
  align-items: center;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  margin-bottom: 24px;
`

const ContentItemValue = styled.p`
  margin-bottom: 0;

  font-size: 32px;
  font-weight: 700;
  font-family: Roboto;
  color: ${({ theme }): string => theme.colors.text.secondary};
`

const ContentItem = styled.div`
  display: grid;
  gap: 8px;

  padding: 24px;

  background-color: ${({ theme }): string => theme.colors.border.white};
  border: 1px solid ${({ theme }): string => theme.colors.border.border};
  border-radius: 6px;

  &:nth-child(n + 3) {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 16px;

    & > ${ContentItemValue} {
      flex-grow: 1;

      font-size: 16px;
      font-weight: 400;
    }
  }
`

const ContentItemColor = styled.span<{ color: string }>`
  display: inline-block;
  width: 16px;
  height: 16px;

  margin-right: 8px;

  background-color: ${({ color }): string => color};
  border-radius: 8px;
`

const ContentItemLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  font-size: 20px;
  font-weight: 700;
  color: ${({ theme }): string => theme.colors.text.primary};
`

export default SlideComponent
